import React, { useEffect, useRef, useState } from 'react';
import { parallelRangeDownload } from '../lib/fastDl';
import logoViaDrive from '../assets/logo_viadrive.svg';
import dlIcon from '../assets/vd_dl_icon.png';

/* ---------- helpers ---------- */
const formatBytes = (bytes = 0) => {
  if (!Number.isFinite(bytes)) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, n = bytes;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 2 : 1)} ${u[i]}`;
};
const formatEta = (sec) => {
  if (sec == null || !Number.isFinite(sec)) return 'Calculando…';
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  if (h) return `${h} h ${m} min`;
  if (m) return `${m} min ${r}s`;
  return `${r}s`;
};

/* ---------- icons ---------- */
const IconSpeed = (p) => (
  <svg viewBox="0 0 24 24" width="16" height="16" {...p}>
    <path fill="currentColor" d="M12 4a9 9 0 00-9 9 1 1 0 001 1h3a1 1 0 001-1 4 4 0 118 0 1 1 0 001 1h3a1 1 0 001-1 9 9 0 00-9-9zm1 9a1 1 0 01-2 0 1 1 0 012 0z"/>
  </svg>
);
const IconClock = (p) => (
  <svg viewBox="0 0 24 24" width="16" height="16" {...p}>
    <path fill="currentColor" d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 11H7v-2h4V6h2v7z"/>
  </svg>
);
const IconCheck = (p) => (
  <svg viewBox="0 0 24 24" width="14" height="14" {...p}>
    <path fill="currentColor" d="M9 16.2l-3.5-3.5L4 14.2l5 5 11-11-1.5-1.5z"/>
  </svg>
);

/* ---------- mini ring (roxo) ---------- */
function MiniRing({ progress = 0, done = false }) {
  const size = 22, stroke = 3;
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, progress));
  return (
    <svg width={size} height={size}>
      <defs>
        <linearGradient id="vdRing" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#7C3AED" />
          <stop offset="100%" stopColor="#A78BFA" />
        </linearGradient>
      </defs>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke}/>
      <g transform={`rotate(-90 ${size/2} ${size/2})`}>
        <circle
          cx={size/2} cy={size/2} r={r} fill="none"
          stroke={done ? '#16a34a' : 'url(#vdRing)'}
          strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${C} ${C}`}
          strokeDashoffset={C - (pct/100)*C}
          style={{transition:'stroke-dashoffset .2s linear'}}
        />
      </g>
    </svg>
  );
}

/* ---------- page ---------- */
export default function ShareFilePage() {
  const [items, setItems] = useState([]);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    (async () => {
      let code = decodeURIComponent(window.location.pathname.split('/f/')[1] || '');
      if (!code) {
        const qs = new URLSearchParams(window.location.search);
        code = qs.get('code');
      }
      if (!code || code === 'undefined' || code === 'null') {
        alert('Link inválido'); return;
      }

      const API = import.meta.env.VITE_BACKEND_URL || 'https://gateway.viadrive.com';
      const res = await fetch(`${API}/open/share/file/${encodeURIComponent(code)}`);
      if (!res.ok) { alert('Falha ao obter metadados'); return; }

      const { presignedUrl, filename, size } = await res.json();
      if (!presignedUrl) { alert('Não foi possível obter o link direto'); return; }

      setItems([{ id: code, filename, size, url: presignedUrl, progress: 0, speed: '0.00', eta: '…', done: false, loaded: 0 }]);

      let ema = 0; // média móvel bps
      const onProgress = (loaded, total, bps) => {
        ema = ema ? ema * 0.85 + bps * 0.15 : bps;
        const pct = (loaded / total) * 100;
        const secs = ema > 0 ? (total - loaded) / ema : null;
        setItems([{
          id: code,
          filename,
          size,
          url: presignedUrl,
          progress: pct,
          speed: ((ema * 8) / 1e6).toFixed(2),
          eta: formatEta(secs ? Math.round(secs) : null),
          done: false,
          loaded
        }]);
        document.title = `(${Math.floor(pct)}%) Baixando • ViaDrive`;
      };

      window.onbeforeunload = () => 'O download será interrompido se fechar essa aba.';
      try {
        await parallelRangeDownload({ url: presignedUrl, filename, size, onProgress });
        setItems(prev => prev.map(d => ({ ...d, progress: 100, eta: 'Concluído', done: true, loaded: d.size })));
        document.title = 'Download concluído • ViaDrive';
      } catch (e) {
        console.error(e);
        window.location.assign(presignedUrl);
      }
      window.onbeforeunload = null;
    })();
  }, []);

  return (
    <div className="min-h-screen text-center" style={{background:'linear-gradient(to bottom,#f8fafc,#fff)'}}>
      <div className="max-w-3xl mx-auto px-6 pt-24 pb-20">
        {/* logo + título */}
        <img src={logoViaDrive} alt="ViaDrive" className="mx-auto mt-10 mb-4" style={{width:200}} />
        <h2 className="text-lg font-semibold tracking-tight mb-4">Download em andamento</h2>

        {/* card */}
        {items.map(d => {
          const pct = d.progress;
          return (
            <div key={d.id} className="mx-auto mt-6 max-w-xl rounded-2xl border border-slate-200" style={{background:'rgba(255,255,255,.9)',backdropFilter:'blur(4px)'}}>
              <div className="p-6">
                {/* header linha */}
                <div className="flex items-center justify-center gap-3 flex-wrap mb-6">
                  <div className="relative">
                    <MiniRing progress={pct} done={d.done}/>
                    {d.done && <div className="absolute inset-0 grid place-items-center"><IconCheck className="text-emerald-600"/></div>}
                  </div>

                  <p className="truncate font-medium text-sm sm:text-base max-w-[45vw]" title={d.filename}>
                    {d.filename}
                  </p>

                  <div className="h-2 w-44 sm:w-56 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full"
                      style={{
                        width: `${pct}%`,
                        background: d.done ? '#16a34a' : 'linear-gradient(90deg,#7c3aed,#a78bfa)',
                        transition:'width .2s linear'
                      }}
                    />
                  </div>

                  <span className="text-xs text-slate-700 tabular-nums">{Math.floor(pct)}%</span>
                </div>

                {/* ícone grande */}
                <img src={dlIcon} alt="Download" className="mx-auto my-5" style={{width:300}} />

                {/* métricas */}
                <div className="flex justify-center gap-4 flex-wrap text-sm text-slate-700">
                  <span className="inline-flex items-center gap-1"><IconSpeed/> {d.speed} Mbps</span>
                  <span className="inline-flex items-center gap-1"><IconClock/> {d.eta}</span>
                </div>

                {/* bytes */}
                <div className="mt-1 text-xs text-slate-500">
                  {formatBytes(d.loaded)} {d.size ? `de ${formatBytes(d.size)}` : ''}
                </div>

                {/* ações */}
                <div className="mt-6 flex justify-center gap-3">
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 rounded-xl border shadow text-black"
                    style={{
                      background: d.done ? '#dcfce7' : '#fff',
                      borderColor: d.done ? '#86efac' : '#cbd5e1'
                    }}
                  >
                    {d.done ? 'Baixar novamente' : 'Baixar diretamente'}
                  </a>
                  <button
                    onClick={async () => { try { await navigator.clipboard.writeText(d.url); alert('Link copiado!'); } catch {} }}
                    className="px-4 py-2 rounded-xl border"
                    style={{borderColor:'#cbd5e1',background:'#fff'}}
                  >
                    Copiar link
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
